import type { ReactNode } from 'react'
import { useNls } from '../../../nls/nlsProvider.js'
import './chart.css'

export type ChartEmbedStatus = 'loading' | 'error' | 'deleted' | 'no-access' | 'empty' | 'ready'

export interface ChartEmbedProps {
  status: ChartEmbedStatus
  errorMessage?: string
  children?: ReactNode
  onRefresh?: () => void
  onExportCsv?: () => void
  onExportPng?: () => void
  className?: string
  testId?: string
}

export function ChartEmbed({
  status,
  errorMessage,
  children,
  onRefresh,
  onExportCsv,
  onExportPng,
  className = '',
  testId,
}: ChartEmbedProps) {
  const { t } = useNls()
  if (status === 'ready') {
    return (
      <div className={`chartEmbedContainer ${className}`} data-testid={testId}>
        {children}
      </div>
    )
  }

  return (
    <div className={`chartEmbedShell ${className}`} data-testid={testId} role="region" aria-live="polite">
      {status === 'loading' && (
        <div className="chartEmbedState chartEmbedStateLoading">
          <div className="chartEmbedMessage">{t('NLSChartStateLoading')}</div>
        </div>
      )}

      {status === 'empty' && (
        <div className="chartEmbedState chartEmbedStateEmpty">
          <div className="chartEmbedMessage">{t('NLSChartStateEmpty')}</div>
        </div>
      )}

      {status === 'error' && (
        <div className="chartEmbedState chartEmbedStateError">
          <div className="chartEmbedMessage">{t('NLSChartStateError')}</div>
          {errorMessage && <div className="chartEmbedErrorDetail">{errorMessage}</div>}
        </div>
      )}

      {status === 'deleted' && (
        <div className="chartEmbedState chartEmbedStateDeleted">
          <div className="chartEmbedMessage">{t('NLSChartStateDeleted')}</div>
        </div>
      )}

      {status === 'no-access' && (
        <div className="chartEmbedState chartEmbedStateNoAccess">
          <div className="chartEmbedMessage">{t('NLSChartStateNoAccess')}</div>
        </div>
      )}

      <div className="chartEmbedActions">
        {onRefresh && (
          <button
            type="button"
            className="chartEmbedRefreshBtn"
            onClick={onRefresh}
            aria-label={t('NLSChartActionRefresh')}
          >
            {t('NLSChartActionRefresh')}
          </button>
        )}
        {onExportCsv && (
          <button
            type="button"
            className="chartEmbedRefreshBtn"
            onClick={onExportCsv}
            aria-label={t('NLSChartActionExportCsv')}
          >
            {t('NLSChartActionExportCsv')}
          </button>
        )}
        {onExportPng && (
          <button
            type="button"
            className="chartEmbedRefreshBtn"
            onClick={onExportPng}
            aria-label={t('NLSChartActionExportPng')}
          >
            {t('NLSChartActionExportPng')}
          </button>
        )}
      </div>
    </div>
  )
}
